import { CheckCircle2 } from 'lucide-react';
import { useCallback, useState } from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { cn } from '@/lib/utils';
import type {
  SubmissionAttemptDestination,
  SubmissionAttemptDestinationOption,
} from '@/services/api/agentflowService';
import { usePatchSubmissionAttemptMutation } from '@/services/api/agentflowService';

const CHANNEL_PREFIXES: Record<string, string> = {
  fax: 'Fax to',
  email: 'Email to',
  mail: 'Mail to',
};

function formatDestinationValue(opt: SubmissionAttemptDestinationOption): string {
  const prefix = CHANNEL_PREFIXES[opt.channel] ?? '';
  return `${prefix} ${opt.value ?? ''}`.trim();
}

interface DestinationSectionProps {
  destination: SubmissionAttemptDestination | null;
  matterId: string;
  requestId: string;
}

export const DestinationSection = ({
  destination,
  matterId,
  requestId,
}: DestinationSectionProps) => {
  const [patchAttempt, { isLoading }] = usePatchSubmissionAttemptMutation();
  const [selectedIndex, setSelectedIndex] = useState<string | null>(null);
  const [showManual, setShowManual] = useState(false);
  const [manualChannel, setManualChannel] = useState('fax');
  const [manualValue, setManualValue] = useState('');

  const handleChoose = useCallback(
    async (opt: SubmissionAttemptDestinationOption) => {
      try {
        await patchAttempt({
          matterId,
          requestId,
          body: {
            action_type: 'choose_destination',
            payload: {
              channel: opt.channel,
              value: opt.value,
              provider_name: opt.provider_name,
              evidence_summary: opt.evidence_summary,
            },
          },
        }).unwrap();
      } catch {
        toast.error('Failed to set destination');
      }
    },
    [matterId, requestId, patchAttempt],
  );

  const handleManualSubmit = useCallback(async () => {
    if (!manualValue.trim()) return;
    try {
      await patchAttempt({
        matterId,
        requestId,
        body: {
          action_type: 'choose_destination',
          payload: {
            channel: manualChannel,
            value: manualValue.trim(),
            provider_name: null,
            evidence_summary: 'Manually entered',
          },
        },
      }).unwrap();
      setShowManual(false);
    } catch {
      toast.error('Failed to set destination');
    }
  }, [matterId, requestId, manualChannel, manualValue, patchAttempt]);

  if (!destination) return null;

  const activeDestination =
    destination.manual_resolution ?? destination.recommended;

  // Build the list of all options for the radio group
  const allOptions: SubmissionAttemptDestinationOption[] = [];
  if (destination.recommended) allOptions.push(destination.recommended);
  allOptions.push(...destination.alternatives);

  // Resolved state
  if (destination.state === 'resolved' && activeDestination) {
    return (
      <div>
        <div className="text-[10px] font-semibold uppercase tracking-wider text-zinc-500 mb-2">
          Destination
        </div>
        <div className="flex items-start gap-2.5">
          <CheckCircle2 className="h-4 w-4 text-green-600 mt-0.5 shrink-0" />
          <div className="min-w-0">
            <div className="text-sm font-medium text-zinc-800 truncate">
              {formatDestinationValue(activeDestination)}
            </div>
            {activeDestination.provider_name && (
              <div className="text-xs text-zinc-500 truncate">
                {activeDestination.provider_name}
                {activeDestination.verified && ' · Verified'}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Unresolved / blocked state
  let manualPlaceholder = 'Enter address...';
  if (manualChannel === 'fax') {
    manualPlaceholder = 'Enter fax number...';
  } else if (manualChannel === 'email') {
    manualPlaceholder = 'Enter email...';
  }

  return (
    <div>
      <div className="text-[10px] font-semibold uppercase tracking-wider text-zinc-500 mb-2">
        Destination
      </div>

      <div className="rounded-lg border border-amber-200 p-4 space-y-4">
        {destination.agent_research_summary && (
          <div className="rounded-md bg-amber-50 px-3 py-2.5">
            <div className="text-[10px] font-medium text-amber-600 mb-1">
              Agent research
            </div>
            <p className="text-xs text-amber-800 leading-relaxed">
              {destination.agent_research_summary}
            </p>
          </div>
        )}

        <RadioGroup
          value={selectedIndex ?? undefined}
          onValueChange={(val) => {
            setSelectedIndex(val);
          }}
          className="gap-0"
        >
          {allOptions.map((opt, i) => (
            <div
              key={i}
              role="button"
              tabIndex={0}
              onClick={() => {
                setSelectedIndex(String(i));
                handleChoose(allOptions[i]);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  setSelectedIndex(String(i));
                  handleChoose(allOptions[i]);
                }
              }}
              className={cn(
                'flex items-start gap-3 rounded-lg border px-4 py-3 cursor-pointer transition-colors',
                selectedIndex === String(i)
                  ? 'border-indigo-300 bg-indigo-50/50'
                  : 'border-zinc-200 hover:bg-zinc-50',
                i > 0 && 'mt-2',
              )}
            >
              <RadioGroupItem value={String(i)} className="mt-0.5" />
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium text-zinc-800">
                  {formatDestinationValue(opt)}
                </div>
                <div className="text-xs text-zinc-500 mt-0.5">
                  {opt.evidence_summary ?? `${opt.channel} destination`}
                </div>
              </div>
            </div>
          ))}
        </RadioGroup>

        {!showManual ? (
          <button
            type="button"
            className="text-xs font-medium text-indigo-600 hover:text-indigo-800"
            onClick={() => setShowManual(true)}
          >
            Search or enter manually
          </button>
        ) : (
          <div className="space-y-2">
            <div className="flex gap-2">
              <select
                value={manualChannel}
                onChange={(e) => setManualChannel(e.target.value)}
                className="rounded-md border border-zinc-200 px-2 py-1.5 text-sm bg-white"
              >
                <option value="fax">Fax</option>
                <option value="email">Email</option>
                <option value="mail">Mail</option>
              </select>
              <Input
                placeholder={manualPlaceholder}
                value={manualValue}
                onChange={(e) => setManualValue(e.target.value)}
                className="flex-1"
              />
              <Button
                size="sm"
                onClick={handleManualSubmit}
                disabled={isLoading || !manualValue.trim()}
              >
                Save
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
